import { FC, useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { colors, spacing, smallShadow } from 'ui/theme';
import { SupportedMediums } from 'modules/frequencyManagement/lib/types';
import { PaginationContextProvider, usePaginationContext } from 'utils/contexts/pagination';
import {
  MESSAGE_TYPES,
  MessageTypeSchema,
} from 'modules/frequencyManagement/contexts/FormProvider';
import { useFiltersContext } from 'modules/frequencyManagement/contexts/FiltersContext';
import { FiltersProvider } from 'modules/frequencyManagement/contexts/FiltersProvider';
import { createFilterBySearchAndFilters } from './utils';
import { CardHeader } from './CardHeader';
import { CardBody } from './CardBody';
import { CardFooter } from './CardFooter';

export const Wrapper = styled.div`
  border-radius: ${spacing.half};
  background-color: ${colors.backgroundPrimary};
  ${smallShadow};
`;

export interface MessageTypesCardProps {
  messageMedium: SupportedMediums;
}
export const BaseMessageTypesCard: FC<MessageTypesCardProps> = ({ messageMedium }) => {
  const messageTypes = useWatch({ name: MESSAGE_TYPES }) as MessageTypeSchema[];
  const { search, filters } = useFiltersContext();
  const { currentPage, setCurrentPage, pageSize, setPageSize } = usePaginationContext();

  const hasMessageTypes = messageTypes.length > 0;

  useEffect(() => {
    setPageSize(10);
  }, [setPageSize]);

  useEffect(() => {
    /** If user enters search or filters, we must reset to page 1 */
    setCurrentPage(1);
  }, [search, filters.size, setCurrentPage]);

  const filteredMessageTypes = messageTypes.filter(createFilterBySearchAndFilters(search, filters));

  const visibleMessageTypes = filteredMessageTypes.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  return (
    <Wrapper data-test={`${messageMedium}-message-type-content`}>
      <CardHeader messageMedium={messageMedium} />
      <CardBody
        messageMedium={messageMedium}
        visibleMessageTypes={visibleMessageTypes}
        hasMessageTypes={hasMessageTypes}
      />
      <CardFooter messageTypes={filteredMessageTypes} />
    </Wrapper>
  );
};

export const MessageTypesCard = ({ ...props }: MessageTypesCardProps) => (
  <FiltersProvider>
    <PaginationContextProvider>
      <BaseMessageTypesCard {...props} />
    </PaginationContextProvider>
  </FiltersProvider>
);
